"use strict";
/**
 * Prototype-pollution-safe record primitives shared across the AI Hub
 * Settings editor.
 *
 * The Settings UI stores user-supplied key/value pairs in plain
 * `Record<string, string>` objects (system config, mapping maps, etc.).
 * Without guards, an attacker-controlled key like `__proto__` could
 * mutate `Object.prototype` (OWASP A03). The helpers here:
 *  - reject reserved keys (`__proto__`, `constructor`, `prototype`),
 *  - allocate prototype-less records via `Object.create(null)`,
 *  - clamp string lengths so a paste-bomb cannot blow up serialization,
 *  - reject non-`http(s)` URLs to defend against stored XSS / SSRF if a
 *    downstream consumer ever passes the value to `<a href>` / `fetch`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeRecord = exports.sanitizeHttpUrl = exports.clampUserString = exports.MAX_USER_STRING_LENGTH = exports.cloneRecord = exports.safeRecord = exports.isSafeRecordKey = void 0;
var FORBIDDEN_RECORD_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
/**
 * @returns Whether `candidate` is safe to use as a user-supplied object key.
 */
exports.isSafeRecordKey = function (candidate) {
    return !FORBIDDEN_RECORD_KEYS.has(candidate);
};
/**
 * Allocate a fresh record with no prototype chain so user-supplied keys
 * cannot collide with built-ins like `toString`, `hasOwnProperty`, etc.
 */
exports.safeRecord = function () {
    return Object.create(null);
};
/**
 * Shallow-clone a record, dropping any reserved keys.
 */
exports.cloneRecord = function (source) {
    var result = exports.safeRecord();
    if (!source) {
        return result;
    }
    Object.entries(source).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        if (exports.isSafeRecordKey(key)) {
            result[key] = value;
        }
    });
    return result;
};
/**
 * Maximum byte length for any single user-supplied string. Caps memory
 * footprint and serialization cost; pasted blobs are silently truncated.
 */
exports.MAX_USER_STRING_LENGTH = 4096;
/**
 * Clamp a value to {@link MAX_USER_STRING_LENGTH} characters; returns empty
 * string for non-string input.
 */
exports.clampUserString = function (value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.length > exports.MAX_USER_STRING_LENGTH ? value.slice(0, exports.MAX_USER_STRING_LENGTH) : value;
};
/**
 * Allow only `http(s)` URLs (and the empty string / scheme-less inputs).
 * Anything with an explicit non-http scheme — `javascript:`, `data:`,
 * `file:`, custom schemes — is rejected.
 */
exports.sanitizeHttpUrl = function (value) {
    var str = exports.clampUserString(value);
    if (!str) {
        return '';
    }
    var schemeMatch = /^([a-z][a-z0-9+\-.]*):/i.exec(str);
    if (schemeMatch && !/^https?$/i.test(schemeMatch[1])) {
        return '';
    }
    return str;
};
/**
 * Trim, drop reserved keys, clamp string lengths. Used by the schema
 * sanitizer for `system.config`, `workflow.config`, `mapping.map`, etc.
 */
exports.sanitizeRecord = function (record) {
    return Object.entries(record || {}).reduce(function (result, _a) {
        var key = _a[0], value = _a[1];
        var normalizedKey = key.trim();
        if (!normalizedKey || !exports.isSafeRecordKey(normalizedKey)) {
            return result;
        }
        result[normalizedKey] = exports.clampUserString(value);
        return result;
    }, exports.safeRecord());
};
